import React, { useState, useEffect, useRef } from 'react';
import { Card, CardBody, Button, Form, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import imageCompression from 'browser-image-compression';
import secureApi from '../../../utils/secureApi';
import TeamsTable from '../../../Common/TeamsTable';
import TeamsHeader from '../../../Common/TeamsHeader';
import { DeleteModal } from '../../../Common/DeleteModal';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import { Team } from '../../../types/Team';
import { getAgeCategoriesFromTournaments, createTeam, CreateTeamRequest, updateTeam, deleteTeam, getTeamsByClubId, TeamData, getTeamById } from '../../../api/club/teamService';
import './ClubDetails.css';
import { getImageUrl } from '../../../utils/imageUrl';

interface TeamListingSectionProps {
  clubId: string;
}

const TeamListingSection: React.FC<TeamListingSectionProps> = ({ clubId }) => {
  const navigate = useNavigate();
  const [teams, setTeams] = useState<TeamData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [ageGroupFilter, setAgeGroupFilter] = useState('all');
  const [ageCategories, setAgeCategories] = useState<string[]>([]);
  const [showAddTeamForm, setShowAddTeamForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);

  // Form state
  const [teamFormData, setTeamFormData] = useState({
    name: '',
    shortName: '',
    logo: '',
    ageGroups: [] as string[],
    coachManagerName: '',
    contactDetails: '',
    sports: 'Football',
    status: 'active'
  });

  // Add state to track original form data for change detection
  const [originalFormData, setOriginalFormData] = useState({
    name: '',
    shortName: '',
    logo: '',
    ageGroups: [] as string[],
    coachManagerName: '',
    contactDetails: '',
    sports: 'Football',
    status: 'active'
  });

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalTeam, setDeleteModalTeam] = useState<Team | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [logoUploadProgress, setLogoUploadProgress] = useState(0);
  const [logoUploadSuccess, setLogoUploadSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const teamFormRef = useRef<HTMLDivElement>(null);

  // Fetch club teams
  const fetchClubTeams = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getTeamsByClubId(clubId);
      
      // Ensure data is an array
      if (Array.isArray(data)) {
        setTeams(data);
      } else {
        console.warn('Unexpected API response structure:', data);
        setTeams([]);
      }
    } catch (err: any) {
      console.error('Error fetching teams:', err);
      setError(err.message || 'Failed to fetch teams');
      setTeams([]);
    } finally {
      setLoading(false);
    }
  };

  // Load age categories - All Clubs specific list
  const loadAgeCategories = async () => {
    // All Clubs module specific age categories
    const allClubsAgeCategories = [
      'U8(Co-Ed)', 
      'U10(Co-Ed)', 
      'U12(Co-Ed)', 
      'Sub Junior Boys', 
      'Junior Boys', 
      'Elite Boys', 
      'U14 Girls', 
      'U17 Girls'
    ];
    setAgeCategories(allClubsAgeCategories);
  };

  useEffect(() => {
    fetchClubTeams();
    loadAgeCategories();
  }, [clubId]);

  // Ensure teams is always an array before filtering
  const teamsArray = Array.isArray(teams) ? teams : [];

  // Map API teams to TeamsTable format
  const mappedTeams: Team[] = teamsArray.map(apiTeam => {
    return {
    _id: apiTeam._id,
    name: apiTeam.name,
    shortName: apiTeam.shortName,
    logo: apiTeam.logo,
      ageGroups: apiTeam.ageCategory ? [apiTeam.ageCategory] : [], // Convert single ageCategory to array
      coachManagerName: '', // API doesn't provide this field
      contactDetails: '', // API doesn't provide this field
      players: apiTeam.playersCount || 0,
      verifiedPlayers: apiTeam.approvedPlayersCount || 0,
      paidPlayers: apiTeam.paidPlayersCount || apiTeam.approvedPlayersCount || 0, // Use paidPlayersCount or fallback to approvedPlayersCount
      sports: 'Football', // Default value like Team Information
    status: 'active'
    };
  });

  // Team action handlers
  const handleAddTeam = () => {
    // Show inline form
    setShowAddTeamForm(true);
  };

  const handleCloseTeamForm = () => {
    setShowAddTeamForm(false);
    setEditingTeam(null);
    // Reset form data
    setTeamFormData({
      name: '',
      shortName: '',
      logo: '',
      ageGroups: [],
      coachManagerName: '',
      contactDetails: '',
      sports: 'Football',
      status: 'active'
    });
    setOriginalFormData({
      name: '',
      shortName: '',
      logo: '',
      ageGroups: [],
      coachManagerName: '',
      contactDetails: '',
      sports: 'Football',
      status: 'active'
    });
    setSubmitError(null);
    setLogoError(null);
    setIsLogoUploading(false);
    setLogoUploadProgress(0);
    setLogoUploadSuccess(false);
  };

  // Handle team added callback
  const handleTeamAdded = () => {
    fetchClubTeams();
    setShowAddTeamForm(false);
    setEditingTeam(null);
    setTeamFormData({
      name: '',
      shortName: '',
      logo: '',
      ageGroups: [],
      coachManagerName: '',
      contactDetails: '',
      sports: 'Football',
      status: 'active'
    });
    setOriginalFormData({
      name: '',
      shortName: '',
      logo: '',
      ageGroups: [],
      coachManagerName: '',
      contactDetails: '',
      sports: 'Football',
      status: 'active'
    });
    setSubmitError(null);
    setLogoError(null);
    setIsLogoUploading(false);
    setLogoUploadProgress(0);
    setLogoUploadSuccess(false);
  };

  // Enhanced form handlers
  const handleTeamFormChange = (field: string, value: string) => {
    setTeamFormData(prev => ({ ...prev, [field]: value }));
  };

  // Check if form has changed (for edit mode)
  const hasFormChanged = () => {
    return JSON.stringify(teamFormData) !== JSON.stringify(originalFormData);
  };

  // Load team data for editing
  const loadTeamDataForEdit = async (team: Team) => {
    try {
      setSubmitError(null);
      setShowAddTeamForm(true);
      setIsEditLoading(true);
      
      // Show loading state for form
      setTeamFormData({
        name: '',
        shortName: '',
        logo: '',
        ageGroups: [],
        coachManagerName: '',
        contactDetails: '',
        sports: 'Football',
        status: 'active'
      });
      
      // Try to fetch detailed team information by ID
      let teamDetails: any = null;
      try {
        teamDetails = await getTeamById(team._id);
        console.log('Team details fetched from API:', teamDetails);
        console.log('Age category from API:', teamDetails.ageCategory);
        console.log('Age category type:', typeof teamDetails.ageCategory);
        console.log('Is age category array:', Array.isArray(teamDetails.ageCategory));
      } catch (apiError: any) {
        console.warn('API failed, using local team data:', apiError.message);
        // Show a warning message to user
        setSubmitError(`Note: Using cached team data. ${apiError.message}`);
        // Fallback to local team data
        console.log('Using fallback data. Team ageGroups:', team.ageGroups);
        console.log('Team ageGroups type:', typeof team.ageGroups);
        console.log('Is team ageGroups array:', Array.isArray(team.ageGroups));
        
        teamDetails = {
          _id: team._id,
          name: team.name,
          shortName: team.shortName,
          logo: team.logo || '',
          managerName: team.coachManagerName || '',
          countryCode: 91,
          managerContactNum: parseInt(team.contactDetails?.replace(/\D/g, '') || '0'),
          location: 'Bengaluru',
          ageCategory: Array.isArray(team.ageGroups) ? team.ageGroups : [],
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
      }
      
      setEditingTeam(team);
      
      // Handle both string and array ageCategory from API
      let finalAgeGroups: string[] = [];
      if (Array.isArray(teamDetails.ageCategory)) {
        finalAgeGroups = teamDetails.ageCategory;
      } else if (typeof teamDetails.ageCategory === 'string') {
        const ageCategoryStr = teamDetails.ageCategory as string;
        if (ageCategoryStr.trim() !== '') {
          finalAgeGroups = [ageCategoryStr];
        }
      }
      console.log('Final ageGroups for form:', finalAgeGroups);
      
      const formData = {
        name: teamDetails.name || '',
        shortName: teamDetails.shortName || '',
        logo: teamDetails.logo || '',
        ageGroups: finalAgeGroups,
        coachManagerName: teamDetails.managerName || '',
        contactDetails: teamDetails.managerContactNum ? teamDetails.managerContactNum.toString() : '',
        sports: 'Football', // Default value as it's not in the API response
        status: 'active' // Default value as it's not in the API response
      };
      
      console.log('Setting form data:', formData);
      console.log('Form data ageGroups:', formData.ageGroups);
      
      setTeamFormData(formData);
      setOriginalFormData(formData); // Set original data for change detection
      
      setLogoError(null);
      setIsLogoUploading(false);
      setLogoUploadProgress(0);
      setLogoUploadSuccess(false);
      
      // Scroll to team form smoothly after form is shown
      setTimeout(() => {
        if (teamFormRef.current) {
          teamFormRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
      
    } catch (error: any) {
      console.error('Failed to load team data for edit:', error);
      setSubmitError(error.message || 'Failed to load team details');
      setShowAddTeamForm(false);
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleTeamLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleTeamLogoFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      setLogoError('Please select a valid image file.');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setLogoError('Image size should be less than 5MB.');
      return;
    }

    try {
      setIsLogoUploading(true);
      setLogoError(null);
      setLogoUploadProgress(0);
      setLogoUploadSuccess(false);

      // Compress image
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 800,
        useWebWorker: true,
        onProgress: (progress) => setLogoUploadProgress(progress)
      });

      // Generate file name and context
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      const ext = compressedFile.type.split('/')[1] || 'jpg';
      const fileName = `team_logo_${Date.now()}.${ext}`;
      const context = `my-club/team-logo/${dd}-${mm}-${yyyy}`;

      // Get presigned URL
      const presignRes = await secureApi.post(
        `/api/v1/general/presigned-url`,
        {
          fileName,
          contentType: compressedFile.type,
          context,
        }
      );

      const presignedUrl = presignRes.data?.presignedUrl;
      const s3Key = presignRes.data?.key;

      if (!presignedUrl || !s3Key) {
        throw new Error('Failed to get presigned URL');
      }

      // Upload to S3
      const uploadRes = await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': compressedFile.type },
        body: compressedFile,
      });
      
      if (!uploadRes.ok) {
        throw new Error('Failed to upload image to S3');
      }

      // Update form data with new logo
      setTeamFormData(prev => ({ ...prev, logo: s3Key }));
      setLogoUploadSuccess(true);
      
    } catch (error) {
      console.error('Error uploading logo:', error);
      setLogoError('Failed to upload logo. Please try again.');
    } finally {
      setIsLogoUploading(false);
    }
  };

  const validateContactNumber = (contact: string): boolean => {
    const phoneRegex = /^[0-9]{10,15}$/;
    return phoneRegex.test(contact.replace(/\D/g, ''));
  };

  const isFormValid = (): boolean => {
    return (
      teamFormData.name.trim() !== '' &&
      teamFormData.shortName.trim() !== '' &&
      teamFormData.ageGroups.length > 0 &&
      teamFormData.contactDetails.trim() !== '' &&
      validateContactNumber(teamFormData.contactDetails)
    );
  };

  const handleSaveTeam = async () => {
    if (!isFormValid()) {
      setSubmitError('Please fill all required fields correctly');
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      if (editingTeam) {
        // Update existing team
        const updateData = {
          name: teamFormData.name,
          shortName: teamFormData.shortName,
          logo: teamFormData.logo,
          managerName: teamFormData.coachManagerName,
          countryCode: 91,
          managerContactNum: parseInt(teamFormData.contactDetails.replace(/\D/g, '')) || 0,
          location: 'Bengaluru',
          ageCategory: teamFormData.ageGroups[0] || '' // Use first age group or empty string
        };

        const response = await updateTeam(editingTeam._id, updateData);

        if (response.success) {
          console.log('Team updated successfully:', response);
          handleTeamAdded();
        } else {
          setSubmitError('Failed to update team. Please try again.');
        }
      } else {
        // Create new team
        const teamData: CreateTeamRequest = {
          name: teamFormData.name,
          shortName: teamFormData.shortName,
          logo: teamFormData.logo,
          managerName: teamFormData.coachManagerName,
          countryCode: 91, // Default to India
          managerContactNum: parseInt(teamFormData.contactDetails.replace(/\D/g, '')) || 0,
          location: 'Bengaluru', // Default location
          ageCategory: teamFormData.ageGroups
        };

        const response = await createTeam(clubId, teamData);

        if (response.success) {
          console.log('Team created successfully:', response);
          handleTeamAdded();
        } else {

          setSubmitError(response.message);
        }
      }
    } catch (error: any) {
      console.error('Error saving team:', error);
      setSubmitError(error.message || 'Error saving team. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Add Players
  const handleAddPlayers = (teamId: string) => {
    navigate(`/all-clubs/${clubId}/team/${teamId}/players`, {
      state: { source: 'add-players' }
    });
  };

  // Handle Manage Players
  const handleManagePlayers = (teamId: string) => {
    navigate(`/all-clubs/${clubId}/team/${teamId}/players`, {
      state: { source: 'manage-players' }
    });
  };

  // Enhanced action handlers
  const handleEditTeam = async (team: Team) => {
    await loadTeamDataForEdit(team);
  };

  const handleDeleteTeam = (teamId: string) => {
    const team = mappedTeams.find(t => t._id === teamId);
    if (team) {
      setDeleteModalTeam(team);
      setShowDeleteModal(true);
      setDeleteError(null);
    }
  };

  // Delete modal handlers
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteModalTeam(null);
    setDeleteError(null);
  };

  const confirmDeleteTeam = async () => {
    if (deleteModalTeam) {
      try {
        console.log('=== Starting delete team process ===');
        setIsDeleting(true);
        setDeleteError(null);
        
        console.log('Attempting to delete team:', deleteModalTeam);
        
        const success = await deleteTeam(deleteModalTeam._id);
        console.log('Delete team result:', success);
        console.log('Success type:', typeof success);
        
        // If we get here without an exception, consider it successful
        // since deleteTeam will throw an exception for actual errors
        
        // Close modal first
        setShowDeleteModal(false);
        setDeleteModalTeam(null);
        setDeleteError(null);
        
        // Refresh teams data instead of reloading page
        await fetchClubTeams();
        
      } catch (error: any) {
        console.error('❌ Exception caught in confirmDeleteTeam:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        
        // Show specific error message from API
        let errorMessage = 'Failed to delete team. Please try again.';
        
        if (error.response?.data) {
          const errorData = error.response.data;
          if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.statusMessage) {
            errorMessage = errorData.statusMessage;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        console.log('Setting error message:', errorMessage);
        setDeleteError(errorMessage);
      } finally {
        console.log('=== Finishing delete team process ===');
        setIsDeleting(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="team-listing-section">
        <TeamsHeader
          title="Team Listing"
          description="All teams under this club"
          searchTerm=""
          onSearchChange={() => {}}
          ageGroupFilter="all"
          onAgeGroupFilterChange={() => {}}
          onAddTeam={() => {}}
          addButtonText="Add New Team"
          showFilters={true}
          ageCategories={[]}
        />
        <ShimmerLoader type="table-rows" variant="team" rows={5} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div className="team-listing-section">
      {/* Use TeamsHeader component for consistent layout */}
      <TeamsHeader
        title="Team Listing"
        description="All teams under this club"
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        ageGroupFilter={ageGroupFilter}
        onAgeGroupFilterChange={setAgeGroupFilter}
        onAddTeam={handleAddTeam}
        addButtonText="Add New Team"
        showFilters={true}
        ageCategories={ageCategories}
      />

      {/* Inline Add Team Form */}
      {showAddTeamForm && (
        <Card className="team-form-card mb-4" ref={teamFormRef}>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">{editingTeam ? 'Edit Team Details' : 'Add Team Details'}</h5>
              <Button 
                variant="link" 
                className="close-form-btn"
                onClick={handleCloseTeamForm}
              >
                <i className="ri-close-line"></i>
              </Button>
            </div>

            {isEditLoading ? (
              <div className="edit-loading-container">
                <ShimmerLoader type="form" />
              </div>
            ) : (
              <>
                <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Team Logo</Form.Label>
                  <div className="team-logo-upload-section">
                    <div className="team-logo-upload">
                      <div 
                        className="team-logo-preview"
                        style={{
                          background: teamFormData.logo ? `url(${getImageUrl(teamFormData.logo)}) center/cover` : '#f8f9fa'
                        }}
                        onClick={handleTeamLogoClick}
                      >
                        {!teamFormData.logo && (
                          <div className="upload-placeholder">
                            <i className="ri-team-line"></i>
                            <div className="small">Upload Team Logo</div>
                          </div>
                        )}
                        {isLogoUploading && (
                          <div className="upload-overlay">
                            <div className="spinner-border spinner-border-sm text-light" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            <div className="small mt-2 text-light">Uploading... {Math.round(logoUploadProgress)}%</div>
                          </div>
                        )}
                      </div>
                      {teamFormData.logo && (
                        <div 
                          className="edit-icon"
                          onClick={handleTeamLogoClick}
                        >
                          <i className="ri-pencil-line"></i>
                        </div>
                      )}
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        onChange={handleTeamLogoFileChange}
                        style={{ display: 'none' }}
                      />
                    </div>
                    
                    {/* Logo upload error and success */}
                    {logoError && (
                      <div className="document-upload-error">
                        <i className="ri-error-warning-line"></i>
                        {logoError}
                      </div>
                    )}
                    
                    {logoUploadSuccess && (
                      <div className="document-upload-success">
                        <i className="ri-check-line"></i>
                        Team logo uploaded successfully!
                      </div>
                    )}
                  </div>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Team Name<span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: '2px' }}>*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={teamFormData.name}
                    onChange={(e) => handleTeamFormChange('name', e.target.value)}
                    placeholder="Enter team name"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Abbreviation<span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: '2px' }}>*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={teamFormData.shortName}
                    onChange={(e) => {
                      // Only allow alphabets and limit to 6 characters
                      const value = e.target.value.replace(/[^a-zA-Z]/g, '').slice(0, 6);
                      handleTeamFormChange('shortName', value);
                    }}
                    placeholder="Enter abbreviation (alphabets only, max 6 chars)"
                    maxLength={6}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Age Categories<span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: '2px' }}>*</span></Form.Label>
                  <Select
                    isMulti={editingTeam === null}
                    classNamePrefix="react-select"
                    value={editingTeam === null 
                      ? (teamFormData.ageGroups || []).map(age => ({ value: age, label: age }))
                      : teamFormData.ageGroups && teamFormData.ageGroups.length > 0 
                        ? { value: teamFormData.ageGroups[0], label: teamFormData.ageGroups[0] }
                        : null
                    }
                    onChange={(selectedOptions) => {
                      if (editingTeam === null) {
                        // Create mode: multi-select
                        const selectedValues = Array.isArray(selectedOptions) ? selectedOptions.map((option: any) => option.value) : [];
                        setTeamFormData(prev => ({ ...prev, ageGroups: selectedValues }));
                      } else {
                        // Edit mode: single-select
                        const selectedValue = selectedOptions && !Array.isArray(selectedOptions) ? (selectedOptions as any).value : '';
                        setTeamFormData(prev => ({ ...prev, ageGroups: selectedValue ? [selectedValue] : [] }));
                      }
                    }}
                    options={ageCategories.map(age => ({ value: age, label: age }))}
                    placeholder={editingTeam === null ? "Select age categories" : "Select age category"}
                    isDisabled={editingTeam !== null && editingTeam.players > 0}
                    maxMenuHeight={400}
                    menuPosition="fixed"
                    menuPortalTarget={document.body}
                    styles={{
                      control: (base) => ({ 
                        ...base, 
                        minHeight: '45px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        boxShadow: 'none'
                      }),
                      menu: (base) => ({ 
                        ...base, 
                        zIndex: 99999,
                        maxHeight: 400,
                        overflow: 'hidden'
                      }),
                      menuPortal: (base) => ({ 
                        ...base, 
                        zIndex: 99999 
                      }),
                      menuList: (base) => ({
                        ...base,
                        maxHeight: 400,
                        padding: 0,
                        overflowY: 'auto',
                        overflowX: 'hidden'
                      }),
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: '#e3f2fd',
                        borderRadius: '6px'
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: '#1976d2',
                        fontWeight: '500'
                      }),
                      multiValueRemove: (base) => ({
                        ...base,
                        color: '#1976d2',
                        ':hover': {
                          backgroundColor: '#bbdefb',
                          color: '#1565c0'
                        }
                      })
                    }}
                  />
                  {editingTeam !== null && editingTeam.players > 0 && (
                    <div className="form-text text-muted mt-1">
                      <i className="ri-information-line me-1"></i>
                      Age category cannot be changed once players are registered to the team
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Coach/Manager Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={teamFormData.coachManagerName}
                    onChange={(e) => handleTeamFormChange('coachManagerName', e.target.value)}
                    placeholder="Enter coach/manager name"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Contact Details<span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: '2px' }}>*</span></Form.Label>
                  <Form.Control
                    type="tel"
                    value={teamFormData.contactDetails}
                    onChange={(e) => handleTeamFormChange('contactDetails', e.target.value)}
                    placeholder="Enter contact number"
                    isInvalid={teamFormData.contactDetails.trim() !== '' && !validateContactNumber(teamFormData.contactDetails)}
                  />
                  {teamFormData.contactDetails.trim() !== '' && !validateContactNumber(teamFormData.contactDetails) && (
                    <Form.Control.Feedback type="invalid">
                      Please enter a valid phone number (10-15 digits)
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>
            
            {submitError && (
              <div className="alert alert-danger mb-3" role="alert">
                <i className="ri-error-warning-line me-2"></i>
                {submitError}
              </div>
            )}
            
            <div className="d-flex justify-content-end">
              <Button variant="secondary" onClick={handleCloseTeamForm} className="me-2" disabled={isSubmitting}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleSaveTeam}
                disabled={!isFormValid() || isSubmitting || (editingTeam !== null && !hasFormChanged())}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {editingTeam ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  editingTeam ? 'Update Team' : 'Add Team'
                )}
              </Button>
            </div>
              </>
            )}
          </CardBody>
        </Card>
      )}

      {/* Use TeamsTable component for consistency */}
      <TeamsTable
        teams={mappedTeams}
        loading={loading}
        showCheckboxes={false}
        showAddPlayers={true}
        showManagePlayers={true}
        showViewDetails={false}
        showEdit={true}
        showDelete={true}
        showPaymentStats={false}
        onAddPlayers={handleAddPlayers}
        onManagePlayers={handleManagePlayers}
        onEdit={handleEditTeam}
        onDelete={handleDeleteTeam}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        ageGroupFilter={ageGroupFilter}
        onAgeGroupFilterChange={setAgeGroupFilter}
        showFilters={false} // Filters are now in header
        showSearch={false} // Search is now in header
        title=""
        emptyMessage="No teams found"
        emptySubMessage="No teams available for this club."
        ageCategories={ageCategories}
      />

      {/* Delete Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={closeDeleteModal}
        deleteModalFunction={confirmDeleteTeam}
        title="Delete Team"
        message={deleteError || `Are you sure you want to delete "${deleteModalTeam?.name}"? This action cannot be undone.`}
        confirmLabel={isDeleting ? "Deleting..." : "Yes, Delete"}
        cancelLabel="Cancel"
        isLoading={isDeleting}
        error={deleteError}
      />
    </div>
  );
};

export default TeamListingSection; 